// src/services/postGrid.js — what a post looks like as one square in a profile grid.
//
// 🟥 THE GRID EXISTS ON FOUR SURFACES AND HAD FOUR ANSWERS. A page's grid on the
//    phone, a page's grid in the browser, and now a person's grid on each — all
//    asking the same three questions and none of them asking it the same way.
//    The phone's page grid borrowed a poster from a post's other media; the
//    browser's had no idea a video could lack one and drew a grey square. This
//    is that decision, once.
//
// 🟥 A VIDEO POST USUALLY HAS NO POSTER FRAME. Nothing in this product ever
//    stored one, so "the thumbnail" for a video is: an image the post happens to
//    carry, or the clip itself, played nowhere and painted at its first frame.
//    Saying that out loud is what stops each surface inventing its own guess.
//
// 🟢 AND VIEWS BELONG TO VIDEOS ONLY. A photo's view count is a number nobody
//    asked for and it would sit on every square in the grid; a video's is the
//    thing people actually want to know about it. One kind, one badge.

/** The audience rule, which is the SAME one the feed and the insights sheet use.
 *
 * 🟥 THERE ARE THREE IMPLEMENTATIONS OF THIS IN THE PRODUCT — `getPostAudience`
 *    on the phone, `postAudience` in the browser, and this one — because the
 *    other two are wired into surfaces that would be a large and unrelated edit
 *    to reroute. They are not allowed to drift: gtest executes all three over the
 *    same matrix and requires them to agree cell for cell. If that test ever
 *    fails, the answer is to make them agree, not to relax the test.
 *
 * 🟢 DEDUPED PER PERSON, AND NEVER THE OWNER. A replay is not a new view, and
 *    legacy rows written before the no-self-views guard existed would otherwise
 *    let somebody inflate their own number by opening their own post.
 */
export function tileViews(post) {
  /* 👁️ the server's count of people who actually looked (viewRoutes.js). The old
     in-post list of the first 500 viewers is no longer the answer: it stopped at
     500 and counted anyone a post scrolled past. */
  return Math.max(0, Number(post?.viewsCount) || 0);
}

/** Is this post a video? One test, so no grid can disagree with another. */
export function isVideoPost(post) {
  const media = Array.isArray(post?.media) ? post.media : [];
  return !!post?.videoUrl || post?.mediaType === 'video' || media.some((m) => m?.type === 'video');
}

/**
 * Does this tile carry a view count?
 *
 * 🟥 VIDEOS ONLY, AND FOR EVERYONE. The feed card shows views to the post's
 *    OWNER alone — that is a deliberate rule about somebody's own analytics and
 *    it stays. This is a different question: a view count under a video in a
 *    grid is what the whole internet shows and what a visitor is looking for.
 *    Photos are excluded because a number on every square is wallpaper, not
 *    information.
 */
export function showsViews(post) {
  return isVideoPost(post);
}

/**
 * The picture for one tile.
 *
 * 🟢 THE ORDER IS: a real still, then a still borrowed from the post's own other
 *    media, then the clip itself. A video posted alongside three photos has a
 *    perfectly good picture available and no reason to fetch bytes for one.
 */
export function gridThumb(post) {
  const p = post || {};
  const media = Array.isArray(p.media) ? p.media : [];
  const video = media.find((m) => m?.type === 'video') || null;
  const isVideo = isVideoPost(p);
  const count = media.length || (p.imageUrl ? 1 : 0);

  /* 🟢 THE RESIZED VARIANT WINS. A grid square is about a third of a phone's
     width; handing it the full-size original is bandwidth spent to draw
     something smaller. The same picker is used for a video's borrowed still,
     because a borrowed still is just as much a thumbnail. */
  const best = (m) => (m ? (m.variants?.medium || m.variants?.small || m.url || null) : null);
  const firstImage = media.find((m) => m?.type !== 'video' && m?.url) || null;
  const still = (video && (video.thumbnail || video.poster))
    || best(firstImage)
    || p.imageUrl
    || null;

  return {
    uri: still || null,
    /* 🟥 ONLY WHEN THERE IS NOTHING ELSE. Painting a frame means fetching the
       opening bytes of the clip; doing that when the post already carries a
       still is bandwidth spent to arrive at the same picture. */
    videoUrl: isVideo && !still ? (video?.url || p.videoUrl || null) : null,
    isVideo,
    count,
  };
}

/**
 * A view count as a badge reads it.
 *
 * 🟥 FOUR DIGITS DO NOT FIT UNDER A THUMBNAIL. "12480" in a 110-point square is
 *    either clipped or shrunk to unreadable, and a grid of them is noise. The
 *    thresholds are the ones every product uses, so nobody has to learn them.
 * 🟢 ONE DECIMAL, AND NEVER A TRAILING ".0" — "1K", not "1.0K".
 */
export function compactViews(n) {
  const v = Number(n) || 0;
  if (v < 1000) return String(v);
  if (v < 1000000) {
    const k = v / 1000;
    return `${k < 10 ? Math.round(k * 10) / 10 : Math.round(k)}K`;
  }
  const m = v / 1000000;
  return `${m < 10 ? Math.round(m * 10) / 10 : Math.round(m)}M`;
}

/* ══ WHAT KIND OF POST IS THIS? ═══════════════════════════════════════════════
   🟥 A POLL WAS DRAWN AS A TEXT POST ON THE PHONE. A poll carries no media and
      no link, so every "is there a picture?" test fell through to the text
      branch and a poll became a quote card showing its own question in curly
      quotes — in the profile grid, and in the preview at the top of the share
      sheet. It is not a text post; it is a thing you can answer, and it looked
      like a thing you could not.

   🟥 THE BROWSER ALREADY KNEW. Its grid has drawn a proper poll tile for a
      while, using `kindOf` over in vibeSearch — so this was one client short of
      a feature rather than a missing feature, which is the worst shape for a
      bug to have: nobody looking at the browser would ever see it.

   🟢 SO THE QUESTION IS ASKED HERE, ONCE, and both clients' tiles and previews
      ask it the same way. The order matters and is the browser's: a poll first,
      because a poll with a photo attached is still a poll. */

export function postKind(post) {
  const p = post || {};
  /* 🟥 A POLL OUTRANKS ITS OWN MEDIA. `type === 'poll'` plus real options is the
     one test — services/vibePoll's `isPollPost`, restated here rather than
     imported so this module keeps no dependencies and can be executed on its
     own in gtest. The two are compared in the test suite. */
  if (p.type === 'poll' && Array.isArray(p.pollOptions) && p.pollOptions.length > 0) return 'poll';
  const media = Array.isArray(p.media) ? p.media : [];
  if (media.some((m) => m?.type === 'video') || p.videoUrl) return 'video';
  if (p.linkUrl) return 'link';
  if (media.length || p.imageUrl) return 'photo';
  return 'text';
}

/**
 * A poll, as a tile or a share preview needs it.
 *
 * 🟥 NO PERCENTAGES AND NO BARS, DELIBERATELY, and this is the whole reason the
 *    shape is defined here rather than left to each surface. Everywhere else in
 *    this product a poll refuses to show its split until you have answered it,
 *    because seeing what the majority already thinks changes what people pick.
 *    A thumbnail is not exempt: a filled bar in a 110-point square leaks exactly
 *    what the feed card is careful not to. The rows are flat, and the only
 *    number is the turnout — which the card shows before you vote as well.
 *
 * 🟥 VOTERS, NOT VOTES. A multiple-choice poll lets one person tick three
 *    options; summing the option arrays reports three people. The distinct set
 *    of uids across every option is the number of humans who answered, which is
 *    what "12 votes" is read as.
 */
export function pollPreview(post, max = 3) {
  const p = post || {};
  const raw = Array.isArray(p.pollOptions) ? p.pollOptions : [];
  const seen = {};
  let voters = 0;
  for (const o of raw) {
    const votes = Array.isArray(o?.votes) ? o.votes : [];
    for (const uid of votes) {
      if (!uid || seen[uid]) continue;
      seen[uid] = true;
      voters += 1;
    }
  }
  const n = Math.max(0, max);
  return {
    /* 🟢 THE QUESTION IS THE POST'S TEXT on a poll built by the composer — the
       phone and the browser both write the draft as `content` — and
       `pollQuestion` is what the admin portal writes. Either is the heading. */
    question: String(p.pollQuestion || p.content || '').trim(),
    options: raw.slice(0, n).map((o, i) => ({
      id: String(o?.id || `i${i}`),
      text: String(o?.text || ''),
    })),
    more: Math.max(0, raw.length - n),
    voters,
  };
}

/* ══ A POST, AS A PREVIEW OF ITSELF ══════════════════════════════════════════
   🟥 THE SHARE SHEET SHOWED A THUMBNAIL AND A CAPTION. You were about to put
      somebody's post on your own feed and the sheet showed you a cropped strip
      of their photo — not who wrote it, not where they were, not whether anyone
      had answered the poll. A share preview that omits the byline is a preview
      of the media, not of the post.

   🟢 SO THE PREVIEW IS THE POST: the same byline, the same words, the same
      poll, the same numbers, in the same order the feed draws them. What each
      client needs to build that is decided here, once, so the phone's and the
      browser's cannot drift into two different quotations of one post. */

/**
 * Who a post is BY.
 *
 * 🟥 A PAGE POST'S BYLINE IS THE PAGE, and its picture is the page's — the
 *    publishing account's name and avatar are still in the document and are
 *    still the wrong thing to draw. This is restated at nearly every byline in
 *    both clients; a preview that got it wrong would credit the wrong account
 *    on a share, which is the one place credit is the whole point.
 */
export function authorOf(post) {
  const p = post || {};
  const isPage = p.authorType === 'page' || !!p.pageId;
  return {
    name: String((isPage ? p.pageName || p.userName : p.userName) || 'Allo user'),
    image: String((isPage ? p.pageImage || p.userImage : p.userImage) || ''),
    isPage,
    pageId: isPage ? (p.pageId || null) : null,
    category: isPage ? (p.pageCategory || null) : null,
  };
}

/**
 * The three numbers under a post.
 *
 * 🟥 SHARES ARE COUNTED TWO WAYS IN THIS PRODUCT and both are real: `shares` is
 *    an array of uids on older documents, `sharesCount` a counter on newer
 *    ones. Reading only one shows 0 for half the archive — and a preview that
 *    says 0 shares about a post with forty is worse than showing nothing.
 */
export function postCounts(post) {
  const p = post || {};
  const arr = (v) => (Array.isArray(v) ? v.length : 0);
  return {
    likes: arr(p.likes),
    /* 📄 a paged post's array is only its old comments — the counter is the total */
    comments: p.commentsV2 === true ? Math.max(arr(p.comments), Number(p.commentsCount) || 0) : arr(p.comments),
    shares: Math.max(arr(p.shares), Number(p.sharesCount) || 0),
  };
}

/* ═══════════════════════════════════════════════════════════════════════════
   🔢 THE NUMBER UNDER A REEL'S ICON
   ═══════════════════════════════════════════════════════════════════════════
   🟥 THE RAIL IS THE ONLY PLACE A REEL IS JUDGED, and the browser's had no
      numbers at all while the phone's had four — so the same clip looked
      popular on one client and unremarked on the other.
   🟢 NOTHING AT ZERO. A column of "0"s under every glyph is four pieces of
      furniture; the icons say what they do without them.
   🟥 AND ONE FORMATTER, so 12400 is "12.4K" on both clients rather than
      "12.4K" on one and "12400" on the other. */
export function shortCount(n) {
  let v = Math.floor(Number(n) || 0);
  if (v <= 0) return '';
  if (v < 1000) return String(v);
  const UNITS = ['K', 'M', 'B'];
  const trim = (x) => x.toFixed(x < 10 ? 1 : 0).replace(/\.0$/, '');
  let i = -1;
  while (v >= 1000 && i < UNITS.length - 1) { v /= 1000; i += 1; }
  let out = trim(v);
  /* 🟥 999,999 IS "1M", NOT "1000K". Rounding can push a number into the next
     unit after the division has already stopped — the obvious version of this
     function ships that bug and nobody sees it until something goes viral. */
  if (Number(out) >= 1000 && i < UNITS.length - 1) { v /= 1000; i += 1; out = trim(v); }
  return `${out}${UNITS[i]}`;
}

/** The four numbers a reel's rail shows, already formatted (empty at zero). */
export function railCounts(post) {
  const p = post || {};
  const arr = (v) => (Array.isArray(v) ? v.length : 0);
  return {
    likes: shortCount(Math.max(arr(p.likes), Number(p.likesCount) || 0)),
    /* the array first, the stored counter second — the same order commentCountOf
       uses, so a rail cannot disagree with the sheet it opens */
    comments: shortCount(p.commentsV2 === true ? Math.max(arr(p.comments), Number(p.commentsCount) || 0) : (arr(p.comments) || Number(p.commentsCount) || 0)),
    shares: shortCount(Math.max(arr(p.shares), Number(p.sharesCount) || 0)),
    saves: shortCount(Math.max(arr(p.savedBy), Number(p.savesCount) || 0)),
  };
}

/**
 * The line under the name: how long ago, and where.
 *
 * 🟢 THE PIECES, NOT THE SENTENCE. Each client formats "2h" its own way — the
 *    phone has a localised relative time and the browser has `ago()` — so what
 *    is shared is which pieces there are and the order they go in, which is the
 *    part that was drifting.
 */
export function previewMeta(post) {
  const p = post || {};
  return {
    locationLabel: p.locationLabel ? String(p.locationLabel) : null,
    audience: p.audience === 'friends' ? 'friends' : null,
  };
}
